import React, { useState } from "react";
/** @jsx jsx */
import { jsx, css, ClassNames } from '@emotion/react';
import { vec2 } from "@tlaukkan/tsm";
import { MouseEventHandler } from "react";
import { ElementDisplayInfo } from "../../../shared/elementDisplayInfo";
import { GameElement } from "../../../shared/gameElementTypes";
import { cellLocations } from '../../../shared/generated/assetLocations';
import { canAffordCell, canAffordImmuneCell, canPlaceCell, hasRoom } from "../../../shared/player";
import { GameClient } from "../gameClient";
import { coloredSpritesheet } from "../rendering/spriteFactory";
import { ImmuneCellButton } from "../styles/ButtonStyle";
import { CostMarker, FreeMarker, TierMarker } from "./TierMarker";
import { AssetLocation } from "../../../shared/utils";
import { Sound } from "../../../shared/sound";
import { PlayerConstants } from "../../../shared/constants";

const dropInAnimation = (index: number, shouldAnimate: boolean, isLocked: boolean) => css`
	@keyframes dropIn {
		0% {
			transform: translateY(-40px);
			opacity: 0;
		}
		100% {
			transform: translateY(0);
			opacity: 1;
		}
	}
	
	@keyframes bobbing {
		0%, 100% {
			transform: translateY(0px);
		}
		50% {
			transform: translateY(-3px);
		}
	}
	
	${shouldAnimate ? css`
		animation: dropIn 0.25s ease-out forwards;
		animation-delay: ${index * 0.1}s;
		opacity: 0;
	` : css`
		opacity: 1;
		transform: translateY(0);
	`}
	
	${isLocked ? css`
		transform: scale(0.9);
		opacity: 0.95;
		
		.cell-image {
			/* No bobbing animation when locked */
		}
	` : css`
		.cell-image {
			animation: bobbing 3s ease-in-out infinite;
			animation-delay: ${index % 2 === 0 ? index * -0.1 : -1.5 + (index * -0.1)}s;
		}
	`}
`;

type AntibodyButtonProps = {
	antibodyType: GameElement,
	index: number,
	flash: boolean,
	flashColors?: [string, string],
	setPurchased: (purchased: boolean) => void,
	tier: number,
	toggleHide: () => void,
	setHovered: (amount: number) => void,
	hoverAntibody: (antibodyType: GameElement, index: number) => void;
	clearHoverAntibody: () => void;
	sendWarning: (type: string) => void;
	shakeGold: (value: boolean) => void;
	flashWarning: (value: boolean) => void;
	isLocked?: boolean;
	isReward: boolean;
	isRewardTier?: boolean;
	enabled?: boolean;
	shouldAnimate?: boolean;
}

declare var gameClient: GameClient;

export const AntibodyButton = ({ enabled, shakeGold, flashWarning, isReward, isRewardTier = false, antibodyType, index, flash, flashColors, setPurchased, toggleHide, hoverAntibody, setHovered, clearHoverAntibody, tier, sendWarning, shouldAnimate = false, isLocked = false }: AntibodyButtonProps) => {
	const info = ElementDisplayInfo.get(antibodyType)!;

	const displayText = info?.displayName || "Unknown";

	const assetLocation = (cellLocations as any)[info.asset] as AssetLocation;
	const spritesheet = coloredSpritesheet;

	const [selected, setSelected] = useState(false)
	const [mouseDownPos, setMouseDownPos] = useState(new vec2());


	const beginBuyPreview: MouseEventHandler<HTMLButtonElement> = (e) => {
		// Check if player can buy, reject if they can't
		if (gameClient) {
			gameClient.playUISound(Sound.ButtonClick);

			const player = gameClient.getPlayer();
			const canAfford = player && canAffordCell(player, isReward);
			const hasCap = player && canPlaceCell(player, antibodyType);
			const canPurchase = canAfford && hasCap;

			// Send warnings
			if (!canAfford) {
				sendWarning('cantAfford');
				shakeGold(true);
			}
			if (!hasCap) {
				sendWarning('nospace');
				flashWarning(true);
				gameClient.tutorialStateMachine.onPopulationLimitReached();
			}

			// Start placement
			if (canPurchase) {
				setSelected(true);
				setMouseDownPos(new vec2([e.nativeEvent.offsetX, e.nativeEvent.offsetY]));
				flashWarning(false);
			}
		}
	}

	const move: MouseEventHandler<HTMLButtonElement> = (e) => {
		if (selected) {
			const mouseCurrentPos = new vec2([e.nativeEvent.offsetX, e.nativeEvent.offsetY]);
			if (vec2.distance(mouseDownPos, mouseCurrentPos) > 10) {
				gameClient.previewBuyAntibody(antibodyType, index, isReward);
				toggleHide();
				setSelected(false);
			}
		}
	}

	const hover = () => {
		if (gameClient) {
			const player = gameClient.getPlayer();
			const canAfford = player && canAffordCell(player, isReward);
			const hasCap = player && canPlaceCell(player, antibodyType);

			// Send warnings
			if (!canAfford) {
				sendWarning('cantAfford');
				shakeGold(true);
			}
			if (!hasCap) {
				sendWarning('nospace');
				flashWarning(true);
			}
		}

		hoverAntibody(antibodyType, index);
		const cost = isReward ? 0 : PlayerConstants.ImmuneCellCost;
		setHovered(cost);
		if (gameClient) {
			gameClient.playUISound(Sound.Menu);
		}
	}

	const cancelSelection = () => {
		setSelected(false);
	}

	const clear = () => {
		clearHoverAntibody();
		setHovered(0);
		cancelSelection();
		flashWarning(false);
	}

	const buttonImageSize = 64;
	const scaleFactor = buttonImageSize / Math.max(assetLocation.width, assetLocation.height);
	const bgSize = spritesheet.width * scaleFactor;
	const cost = isReward ? 0 : PlayerConstants.ImmuneCellCost;

	const player = gameClient.getPlayer();
	const canAfford = player && canAffordCell(player, isReward);
	const hasCap = player && canPlaceCell(player, antibodyType);
	const canPurchase = canAfford && hasCap;

	return (
		<ImmuneCellButton
			disabled={!enabled}
			tabIndex={-1}
			flash={canPurchase && flash}
			flashColors={flashColors}
			softDisable={!canPurchase}
			title={displayText}
			className="market-button goo-button"
			onMouseDown={beginBuyPreview}
			onMouseUp={cancelSelection}
			onMouseMove={move}
			onMouseEnter={hover}
			onMouseLeave={clear}
			css={dropInAnimation(index, shouldAnimate, isLocked)}
		>
			<div
				className="cell-image"
				style={{
					backgroundImage: `url(${spritesheet.src})`,
					backgroundPosition: `${assetLocation.x * scaleFactor}px ${-assetLocation.y * scaleFactor}px`,
					backgroundSize: `${bgSize}px`,
					display: "inline-block",
					width: `${assetLocation.width * scaleFactor}px`,
					height: `${assetLocation.height * scaleFactor}px`,
				}}></div>
			<TierMarker position={'bottom-right'} tier={tier} isReward={isRewardTier} />
			{cost > 0 ?
				<CostMarker cost={cost} position="top-left" />
				:
				<FreeMarker position="top-left" />
			}
		</ImmuneCellButton >);
}
